/**
 * How much room the interface takes.
 *
 * Three settings, differing in density rather than in colour: spacious (what
 * the app shipped with), compact, and tabular. The choice is kept in two
 * places at once — in localStorage so it is on screen before the first paint,
 * and on the user's account so it follows them to another browser. The stored
 * copy is applied at boot; the account's overrides it as soon as /api/me
 * answers.
 *
 * The list here has to match resources/css/themes.css and config/themes.php —
 * the config one because it is what the API will accept.
 */

export const DEFAULT_THEME = 'spacious';

export const THEMES = [
  // Spacious has no rules in themes.css at all: choosing it applies nothing
  // and leaves the original exactly as it is.
  { key: 'spacious', density: null },
  { key: 'compact', density: 'compact' },
  { key: 'tabular', density: 'tabular' },
];

const STORAGE_KEY = 'theme';

/** Settings that existed before this was about density, mapped to their heirs. */
const RENAMED = {
  classic: 'spacious',
  indigo: 'compact',
  graphite: 'spacious',
};

export function normaliseTheme(value) {
  const key = RENAMED[value] ?? value;

  return THEMES.some((theme) => theme.key === key) ? key : DEFAULT_THEME;
}

export function isTheme(value) {
  return THEMES.some((theme) => theme.key === value);
}

/**
 * Paint the setting.
 *
 * Density goes on its own attribute so the stylesheet can address the two
 * dense settings together — html[data-density] matches both — while tabular's
 * extras address its value. Spacious removes the attribute rather than
 * setting it to a value with no rules behind it.
 */
export function applyTheme(theme) {
  const key = normaliseTheme(theme);
  const { density } = THEMES.find((t) => t.key === key);

  if (density) {
    document.documentElement.setAttribute('data-density', density);
  } else {
    document.documentElement.removeAttribute('data-density');
  }

  return key;
}

export function readStoredTheme() {
  try {
    return normaliseTheme(localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private windows and blocked site data throw rather than return null.
    return DEFAULT_THEME;
  }
}

export function storeTheme(theme) {
  try {
    localStorage.setItem(STORAGE_KEY, normaliseTheme(theme));
  } catch {
    // Not being able to remember it is not a reason to fail applying it.
  }
}

/** Apply whatever was last chosen on this device. Called before the app mounts. */
export function initTheme() {
  return applyTheme(readStoredTheme());
}
